import * as React from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import FieldAdminTopBar from "./FieldAdminTopBar";
import FieldAdminBottomNav from "./FieldAdminBottomNav";
import { BG } from "../../../Global/colors";
import { SCALE, readLargerText, subscribeLargerText } from "../textScale";

/**
 * Owns the dark ground for the whole Field Admin section.
 *
 * Mounted once, at the top of MainRouter, and driven by the location rather than by the
 * layout below — because MainRouter gives each route its own <FieldAdminLayout> element,
 * so the layout unmounts and remounts on every navigation. Anything that painted the
 * background from there would lift it mid-navigation, which is the white flash itself:
 * pages return null while their query resolves, so with no body background there is
 * nothing behind them but UA white, framed by the still-dark fixed bars.
 *
 * Scoped to /field-admin rather than applied globally: MainRouter also serves the
 * Installer app and login, which are light-themed. QE is dark, but paints its own ground
 * from QE/Dashboard.js rather than from here. colorScheme darkens native
 * scrollbars and pickers, matching the <meta name="color-scheme" content="dark"> the
 * design pages carry. public/index.html covers the pre-React window on a hard refresh.
 *
 * It also carries the Profile page's "Larger text" preference, for the same reasons and with
 * the same scoping: the root font-size is what every `rem` in the feature resolves against,
 * and it must be lifted on the way out so the Installer app is not left oversized. Read from
 * localStorage in the initialiser rather than after mount, so a cold load paints at the right
 * size instead of reflowing once.
 */
export function FieldAdminBackground() {
  const dark = useLocation().pathname.startsWith("/field-admin");
  const [largerText, setLargerText] = React.useState(readLargerText);

  React.useEffect(() => subscribeLargerText(setLargerText), []);

  React.useEffect(() => {
    const html = document.documentElement;
    html.style.background = dark ? BG : "";
    html.style.colorScheme = dark ? "dark" : "";
    html.style.fontSize = dark && largerText ? SCALE : "";
    document.body.style.background = dark ? BG : "";
  }, [dark, largerText]);

  return null;
}

export default function FieldAdminLayout({ crumb, backTo, hideBack, hideTopBar, children }) {
  return (
    <>
      {hideTopBar ? null : <FieldAdminTopBar crumb={crumb} backTo={backTo} hideBack={hideBack} />}
      <FieldAdminBottomNav />
      <Box sx={{ backgroundColor: BG, minHeight: "100vh" }}>{children}</Box>
    </>
  );
}
